/**
 * @fileoverview Adapter that maps live renderer/host state (forms + resolved
 *   states + persisted responses + logged violation occurrences) into the
 *   host-agnostic `CompletionValidationInput` consumed by the single canonical
 *   `validateInspectionForCompletion` process in `@firesquirrel/core-domain`.
 * @module @firesquirrel/inspection-renderer/validation/completionInput
 *
 * Responsibilities:
 * - Provide ONE place that both the in-package `InspectionRenderer` and the
 *   host conductors (tenant-web, mobile-pwa) use to assemble completion input,
 *   so the "what data goes into the gate" shaping never drifts between hosts.
 *
 * Dependencies:
 * - `@firesquirrel/core-domain` — CompletionValidationInput / CompletionFormInput types.
 * - `../violations/types` — LoggedViolation (renderer-side occurrence shape).
 *
 * Architecture:
 * - Pure function. No React, no Dexie, no network. Deterministic given inputs.
 *
 * Notes:
 * - Completion gating itself lives in core-domain; this module only adapts
 *   data shapes. It must never decide can/cannot-complete.
 */
import type {
  InspectionForm,
  ResolvedFormStateV2,
  FieldResponse,
  CompletionValidationInput,
  CompletionFormInput,
  FieldOccurrenceInput,
} from '@firesquirrel/core-domain';
import type { LoggedViolation } from '../violations/types';

/**
 * @process buildCompletionValidationInput
 *
 * Steps:
 * 1. For each form, read its persisted responses via `getValues(formId)`.
 * 2. Group every logged occurrence under the field that raised it
 *    (`violation.formId` + `violation.fieldId`), projecting each occurrence to
 *    the host-agnostic `FieldOccurrenceInput` (location/notes/correctiveActions
 *    + `attachmentCount`).
 * 3. Attach the engine-v2 resolved state for the form (form/section/field
 *    hidden + required), which carries the hidden / N/A completion exceptions.
 *
 * Inputs: all template forms, the resolved-state map, a per-form values reader,
 *   and the flat list of logged violations.
 * Outputs: a `CompletionValidationInput` ready for `validateInspectionForCompletion`.
 *
 * Failure Handling: pure/no-throw; missing maps degrade to empty objects.
 * Idempotency: REQUIRED — same inputs always yield the same output.
 */
export function buildCompletionValidationInput(
  forms: readonly InspectionForm[],
  resolvedStates: Record<string, ResolvedFormStateV2>,
  getValues: (formId: string) => Record<string, FieldResponse>,
  violations: readonly LoggedViolation[],
): CompletionValidationInput {
  const completionForms: CompletionFormInput[] = forms.map((form) => {
    const occurrencesByField: Record<string, FieldOccurrenceInput[]> = {};
    for (const v of violations) {
      if (v.formId !== form._id || !v.fieldId) continue;
      const list = occurrencesByField[v.fieldId] ?? (occurrencesByField[v.fieldId] = []);
      for (const occ of v.occurrences) {
        list.push({
          location: occ.location,
          notes: occ.notes,
          correctiveActions: occ.correctiveActions,
          attachmentCount: occ.attachments?.length ?? 0,
        });
      }
    }
    return {
      form,
      responses: getValues(form._id),
      resolved: resolvedStates[form._id],
      occurrencesByField,
    };
  });
  return { forms: completionForms };
}
